import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { MiniKit } from '@worldcoin/minikit-js';
import { debug } from '../utils/DebugLogger';

interface WorldAppContextType {
  isWorldApp: boolean;
  isMiniKitReady: boolean;
}

const WorldAppContext = createContext<WorldAppContextType | undefined>(undefined);

export const useWorldApp = (): WorldAppContextType => {
  const context = useContext(WorldAppContext);
  if (context === undefined) {
    throw new Error('useWorldApp must be used within a WorldAppProvider');
  }
  return context;
};

interface WorldAppProviderProps {
  children: ReactNode;
}

export const WorldAppProvider: React.FC<WorldAppProviderProps> = ({ children }) => {
  const [isWorldApp, setIsWorldApp] = useState(false);
  const [isMiniKitReady, setIsMiniKitReady] = useState(false);

  useEffect(() => {
    const checkWorldApp = () => {
      try {
        const globalMiniKit = (window as any).MiniKit;
        if (!globalMiniKit) {
          setIsWorldApp(false);
          setIsMiniKitReady(true);
          debug.info('WorldAppContext', 'MiniKit not available, running in web browser');
          return;
        }

        if (typeof globalMiniKit.isInstalled !== 'function') {
          setIsWorldApp(false);
          setIsMiniKitReady(true);
          debug.info('WorldAppContext', 'MiniKit.isInstalled not available, running in web browser');
          return;
        }

        let detected = false;
        try {
          detected = MiniKit.isInstalled();
        } catch (isInstalledError) {
          console.warn('⚠️ [WorldAppContext] MiniKit.isInstalled() threw error:', isInstalledError);
          detected = false;
        }

        setIsWorldApp(detected);
        setIsMiniKitReady(true);

        if (detected) {
          debug.success('WorldAppContext', 'Running in World App environment');
        } else {
          debug.info('WorldAppContext', 'Running in web browser environment');
        }
      } catch (error) {
        console.warn('⚠️ [WorldAppContext] Environment check failed, defaulting to web browser:', error);
        setIsWorldApp(false);
        setIsMiniKitReady(true);
      }
    };

    const timeouts = [
      setTimeout(checkWorldApp, 100),
      setTimeout(checkWorldApp, 500),
      setTimeout(checkWorldApp, 1000),
      setTimeout(checkWorldApp, 2000),
    ];

    return () => {
      timeouts.forEach(id => clearTimeout(id));
    };
  }, []);

  return (
    <WorldAppContext.Provider value={{ isWorldApp, isMiniKitReady }}>
      {children}
    </WorldAppContext.Provider>
  );
};
